import type { ReactNode } from 'react';

// ── Platforms ──────────────────────────────────────────────────
//
// Publish targets, keyed by the API's `platform` id. Deliberately separate
// from `PLATFORMS` in lib/utils/platforms.ts: that registry names media
// *origins* (Vimeo, Dropbox, Drive) and feeds the library filters, so an
// entry there is a promise to every `PlatformKey` consumer.

/** Platforms the API can connect and post to today. Grows one value per shipped connector. */
export type SocialPlatform = 'tiktok' | 'facebook' | 'youtube';

/** Platforms shown as "coming soon": a disabled connector with no flow behind it. */
export type UpcomingSocialPlatform = 'instagram' | 'linkedin' | 'x';

export type SocialConnectorPlatform = SocialPlatform | UpcomingSocialPlatform;

/** Brand tint for a platform's cards and chips, picked for the dark UI. */
export type SocialPlatformColors = { bg: string; text: string };

/** What every connector, shipped or upcoming, presents. */
export type SocialConnectorPresentation = {
  label: string;
  icon: ReactNode;
  colors: SocialPlatformColors;
  /**
   * A line under the connect modal's list about what the platform's consent
   * page does before the user is back: set while a platform's app review is
   * pending, removed with it. Each module's own `FIXME` names the review.
   */
  connectNote?: string;
};

/** The page a connect started from, for tracking; each one is a `returnTo` the platform sends the browser back to. */
export type SocialConnectSource = 'settings' | 'schedule' | 'compose';

/** The surfaces that open the schedule's time zone dialog; the tracking event carries which. */
export type ScheduleTimeZoneSource = 'compose' | 'schedule';

// Discriminated on `available` so a connect handler can only ever receive a
// platform the API knows how to connect.
export type SocialConnector = SocialConnectorPresentation &
  (
    | { platform: SocialPlatform; available: true }
    | { platform: UpcomingSocialPlatform; available: false }
  );

// ── Post content ───────────────────────────────────────────────

/** A post's audience, as the rows word it; `unlisted` is YouTube's link-only level. */
export type PostPrivacy = 'public' | 'unlisted' | 'private';

export const POST_PRIVACY_LABELS: Record<PostPrivacy, string> = {
  public: 'Public',
  unlisted: 'Unlisted',
  private: 'Private',
};

/**
 * The generic reading of a post's platform-exact content: what a row shows
 * regardless of platform. The module words the headline, drafts included
 * ("Send to TikTok inbox", "Draft: …"), so the rows never spell a platform's
 * draft out themselves. `privacy` is the post's audience in one vocabulary
 * across platforms (TikTok's five levels and YouTube's three map onto it), or
 * null when the post offers no such choice (a TikTok inbox draft carries none;
 * a Facebook Page post is public with nothing to choose). `variant` is the
 * platform's kind of post when it has more than one ("Reel", "Video"), shown
 * in the row meta.
 */
export type PostContentSummary = {
  headline: string;
  privacy: PostPrivacy | null;
  /** The post stays unpublished on the platform: a TikTok inbox draft, a Facebook Page draft. */
  draft: boolean;
  variant: string | null;
};

// ── Capabilities ───────────────────────────────────────────────

/**
 * The platform's verdict on the account (capped for the day, banned) instead
 * of its settings: word `code` and block the account. The `available: true`
 * arm is each module's own (`SocialAccountCapabilities` in the registry).
 */
export type SocialAccountUnavailable = { available: false; code: string };

// ── Platform module ────────────────────────────────────────────

/**
 * `onChange` is declared in method syntax, like the module's members below:
 * a callback-typed property would make `ComposeFieldsProps<A>` unassignable
 * to `ComposeFieldsProps<A | B>`, which the registry's erased entry type needs.
 */
export type ComposeFieldsProps<TContent, TCapabilities> = {
  content: TContent;
  onChange(content: TContent): void;
  /** Null while the account's capabilities are loading, failed to load, or came back as a verdict: the form then offers the platform's defaults. */
  capabilities: TCapabilities | null;
  /** A post the status no longer lets the user edit renders its fields read-only. */
  disabled: boolean;
};

/**
 * One module per shipped platform: everything the scheduling UI needs to
 * describe, translate and compose a post for it. `TContent` is the
 * platform-exact content shape the list returns and edit takes back whole;
 * `TCapabilities` the `available: true` answer of the capabilities read.
 *
 * Every member taking `TContent` or `TCapabilities` is declared in method
 * syntax on purpose: method parameters are checked bivariantly, so a
 * `SocialPlatformModule<TikTokPostContent, …>` is assignable to the registry's
 * erased entry type. `ComposeFields` is a function component, and a function
 * is what JSX takes as an element type.
 */
export type SocialPlatformModule<
  TContent extends { platform: SocialPlatform },
  // Not `TContent['platform']`: the registry's mapped type pins both to the same key, and TypeScript cannot prove two `Extract`s agree.
  TCapabilities extends { platform: SocialPlatform; available: true },
> = SocialConnectorPresentation & {
  platform: TContent['platform'];
  /** What one connection is on this platform, in copy: "account", or "Page" on Facebook. Capitalised as the platform writes it. */
  accountNoun: string;
  /** What disconnecting does beyond forgetting the account here, appended to the confirm dialog: whether the platform's own grant goes too. */
  disconnectNote: string;
  /** The connect flow's `no_accounts` outcome in the platform's terms: what the login lacked, and what to allow next time. */
  noAccountsNote: string;
  /**
   * The list's `content` for one of this platform's posts, as the API sent it.
   * Throws on a shape that is not this platform's: a post whose `platform`
   * and content disagree is a backend bug, not a state to render.
   */
  parseContent(raw: unknown): TContent;
  describeContent(content: TContent): PostContentSummary;
  /**
   * What a freshly ticked account starts from: a publishable post with
   * nothing chosen that the user must choose (TikTok leaves the privacy
   * level unset, so the form lists it as missing rather than presuming).
   */
  defaultContent(): TContent;
  /**
   * Why the content cannot be scheduled yet, as copy under the submit
   * button, or null when it can. `capabilities` is null while unknown: the
   * platform then answers for its API as a whole.
   */
  missingField(content: TContent, capabilities: TCapabilities | null): string | null;
  /**
   * The `available: true` answer of `GET /social/connections/{id}/capabilities`
   * for one of this platform's accounts. Throws on another platform's shape,
   * like `parseContent`.
   */
  parseCapabilities(raw: unknown): TCapabilities;
  /** The line under the account's name in the form: what it may post, in the platform's own terms. */
  describeCapabilities(capabilities: TCapabilities): string;
  /** The platform-specific part of the compose form. */
  ComposeFields(props: ComposeFieldsProps<TContent, TCapabilities>): ReactNode;
};
